import React, { useEffect, useRef, useState } from 'react'
import { DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import axios from 'axios'
import { Button } from '../ui'
import { downloadProductStockExcel } from '../../services/productStockExportService'
import { extractAxiosApiErrorMessage } from '../../utils/apiErrorMessage'

interface ExportProductStockButtonProps {
  roleId: number | undefined | null
}

function isAbortError(error: unknown): boolean {
  if (axios.isCancel(error)) return true
  if (!error || typeof error !== 'object') return false
  const name = 'name' in error ? String(error.name) : ''
  const code = 'code' in error ? String(error.code) : ''
  return name === 'AbortError' || name === 'CanceledError' || code === 'ERR_CANCELED'
}

const ExportProductStockButton: React.FC<ExportProductStockButtonProps> = ({ roleId }) => {
  const [isExporting, setIsExporting] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const abortRef = useRef<AbortController | null>(null)
  const mountedRef = useRef(true)
  const runningRef = useRef(false)

  useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
      abortRef.current?.abort()
    }
  }, [])

  const closeDialog = () => {
    abortRef.current?.abort()
    abortRef.current = null
    if (!mountedRef.current) return
    setIsExporting(false)
    setErrorMessage(null)
  }

  const runExport = async () => {
    if (runningRef.current) return
    runningRef.current = true
    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller
    setIsExporting(true)
    setErrorMessage(null)

    try {
      const result = await downloadProductStockExcel(roleId, controller.signal)
      if (!mountedRef.current || controller.signal.aborted) return
      setIsExporting(false)
      const countLabel = result.productCount.toLocaleString('es-CO')
      if (result.flexyProductCount === 0) {
        toast(
          `Se descargó el archivo de ${countLabel} productos, pero la consulta de stock no trajo saldos.`,
          { icon: '⚠️', duration: 7000 }
        )
        return
      }
      toast.success(`Se descargó el stock actual de ${countLabel} productos.`)
    } catch (error: unknown) {
      if (!mountedRef.current || isAbortError(error) || controller.signal.aborted) return
      setIsExporting(false)
      setErrorMessage(
        extractAxiosApiErrorMessage(error, 'No se pudo exportar el stock. Intenta de nuevo.')
      )
    } finally {
      runningRef.current = false
    }
  }

  const dialogOpen = isExporting || Boolean(errorMessage)

  return (
    <>
      <Button
        type="button"
        variant="primary"
        outline
        leftIcon={DocumentArrowDownIcon}
        iconClassName="h-6 w-6"
        className="border bg-white"
        isLoading={isExporting && !errorMessage}
        onClick={() => {
          void runExport()
        }}
        title="Descarga el stock actual de todos los productos. La consulta se hace al pulsar."
        aria-label="Exportar stock actual de todos los productos"
      >
        Exportar stock
      </Button>

      {dialogOpen && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="export-stock-title"
          aria-describedby="export-stock-message"
          aria-busy={isExporting && !errorMessage}
        >
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 id="export-stock-title" className="text-lg font-semibold text-gray-900">
              Exportar stock
            </h3>
            <p id="export-stock-message" className="mt-2 text-sm text-gray-600" aria-live="polite">
              {errorMessage
                ? 'No se generó el archivo.'
                : 'Consultando el saldo actual de todos los productos.'}
            </p>

            {errorMessage ? (
              <div
                role="alert"
                className="mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800"
              >
                {errorMessage}
              </div>
            ) : (
              <div className="mt-5 flex justify-center" aria-hidden="true">
                <div className="h-14 w-14 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
              </div>
            )}

            <div className="mt-6 flex justify-end gap-2">
              <Button type="button" variant="secondary" onClick={closeDialog}>
                {errorMessage ? 'Cerrar' : 'Cancelar'}
              </Button>
              {errorMessage && (
                <Button
                  type="button"
                  variant="primary"
                  onClick={() => {
                    void runExport()
                  }}
                >
                  Reintentar
                </Button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default ExportProductStockButton
